import React from "react";

import moment from "moment-timezone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons";

import { formatTimeAgo, formatTimestampLong } from "utils/format";

import { OrganizationIntegrationsQuery } from "../../gql/Query.generated";

// Health fields shared by every kind of endpoint the webhook pipeline delivers to
export type EndpointHealthType = Pick<
  OrganizationIntegrationsQuery["getOrganizationDetails"]["webhookEndpoints"][number],
  | "disabledAt"
  | "disabledReason"
  | "consecutiveFailures"
  | "lastSuccessAt"
  | "lastFailureAt"
>;

export function EndpointHealthSummary({
  endpoint,
}: {
  endpoint: EndpointHealthType;
}) {
  return (
    <EndpointStatus
      endpoint={endpoint}
      fallback={
        <span className="text-[#606771]">
          {endpoint.lastSuccessAt != null ? (
            <>
              Delivered <TimeAgo ts={endpoint.lastSuccessAt} />
            </>
          ) : (
            "No alerts delivered yet"
          )}
        </span>
      }
    />
  );
}

// A warning when the endpoint is disabled or failing, otherwise the fallback
export function EndpointStatus({
  endpoint,
  fallback = null,
}: {
  endpoint: EndpointHealthType;
  fallback?: React.ReactNode;
}) {
  const status = endpointStatus(endpoint);
  if (!status) {
    return <>{fallback}</>;
  }
  return (
    <span className={status.className}>
      <FontAwesomeIcon className="mr-1" icon={faExclamationTriangle} />
      {status.text}
    </span>
  );
}

export function TimeAgo({ ts }: { ts: number }) {
  const time = moment.unix(ts);
  return <span title={formatTimestampLong(time)}>{formatTimeAgo(time)}</span>;
}

export function lastDeliveriesDescription(endpoint: EndpointHealthType) {
  const describe = (ts: number | null | undefined) =>
    ts == null ? "never" : formatTimeAgo(moment.unix(ts));
  return `Last delivered: ${describe(
    endpoint.lastSuccessAt,
  )}. Last failed: ${describe(endpoint.lastFailureAt)}.`;
}

function endpointStatus(endpoint: EndpointHealthType) {
  if (endpoint.disabledAt) {
    return endpoint.disabledReason === "delivery_failures"
      ? {
          text: "Disabled after repeated delivery failures",
          className: "text-[#c93a2a]",
        }
      : { text: "Disabled", className: "text-[#606771]" };
  }
  if (endpoint.consecutiveFailures > 0) {
    return { text: "Recent deliveries failed", className: "text-[#c93a2a]" };
  }
  return null;
}
